import { useState } from "react";
import { ArrowUpRight, Mail, MapPin, Globe, Instagram, Facebook, Youtube, Send } from "lucide-react";
import { Reveal, MaskLines } from "../components/Reveal";
import { FILMS } from "../data/films";

const INQUIRY_TYPES = [
  "Film Festival",
  "Distribution",
  "Co-Production",
  "Investment",
  "Media and Press",
  "Casting",
  "General Inquiry",
];

const PRIMARY_EMAIL = "dharakala@outlook.com";

export default function Contact() {
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    organization: "",
    country: "",
    inquiryType: INQUIRY_TYPES[6],
    project: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [newsletter, setNewsletter] = useState("");
  const [newsletterOk, setNewsletterOk] = useState(false);

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = "Required";
    if (!form.email.trim()) e.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Invalid email";
    if (!form.message.trim()) e.message = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    const body = encodeURIComponent(
      `Full Name: ${form.fullName}\n` +
      `Email: ${form.email}\n` +
      `Organization: ${form.organization}\n` +
      `Country: ${form.country}\n` +
      `Inquiry Type: ${form.inquiryType}\n` +
      `Project of Interest: ${form.project}\n\n` +
      `Message:\n${form.message}`
    );
    const subject = encodeURIComponent(`[${form.inquiryType}] ${form.fullName}`);
    window.location.href = `mailto:${PRIMARY_EMAIL}?subject=${subject}&body=${body}`;
    setSubmitted(true);
  };

  const submitNewsletter = (ev) => {
    ev.preventDefault();
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newsletter)) {
      setNewsletterOk(true);
      setNewsletter("");
    }
  };

  return (
    <main data-testid="contact-page" className="bg-black pt-32 md:pt-40 min-h-screen">
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16">
        <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] mb-6">Contact</p>
        <h1 className="font-serif-display font-light text-white text-[13vw] md:text-[9vw] leading-[0.9] tracking-tighter">
          <MaskLines lines={["Let's"]} />
          <span className="block italic text-[color:var(--dk-gold)]">
            <MaskLines lines={["talk."]} delay={0.35} />
          </span>
        </h1>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        {/* Left: Contact info */}
        <Reveal className="lg:col-span-5">
          <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] mb-6">Primary Correspondence</p>
          <a data-testid="contact-primary-email" href={`mailto:${PRIMARY_EMAIL}`}
            className="group inline-flex items-baseline gap-3 flex-wrap">
            <span className="font-serif-display italic text-white text-3xl md:text-5xl leading-tight group-hover:text-[color:var(--dk-gold)] transition-colors break-all">
              {PRIMARY_EMAIL}
            </span>
            <ArrowUpRight size={24} className="text-[color:var(--dk-gold)]" />
          </a>
          <p className="mt-4 text-white/50 text-sm max-w-md leading-relaxed">
            All inquiries — festivals, distribution, co-production, investment, media, casting — reach us at this single address.
          </p>

          <div className="mt-14 space-y-8">
            <InfoRow icon={<Globe size={16} />} label="Website" value="dharakalamovies.com" />
            <InfoRow icon={<MapPin size={16} />} label="Studio" value="New Baneshwor, Kathmandu, Nepal" sub="Established 2019 · Second unit — Dharan · Halesi · Canada" />
            <InfoRow icon={<Mail size={16} />} label="Email" value={PRIMARY_EMAIL} />
          </div>

          <div className="mt-14">
            <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] mb-4">Social</p>
            <div className="flex gap-3">
              {[
                { icon: Instagram, label: "instagram", href: "#" },
                { icon: Facebook, label: "facebook", href: "#" },
                { icon: Youtube, label: "youtube", href: "#" },
              ].map((s, i) => (
                <a key={i} href={s.href} data-testid={`social-${s.label}`}
                  className="w-11 h-11 border border-white/20 flex items-center justify-center text-white/70 hover:border-[color:var(--dk-gold)] hover:text-[color:var(--dk-gold)] transition-colors">
                  <s.icon size={16} />
                </a>
              ))}
            </div>
          </div>
        </Reveal>

        {/* Right: Form */}
        <Reveal delay={1} className="lg:col-span-7">
          <div className="border border-white/10 bg-[color:var(--dk-muted)] p-6 md:p-10">
            <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] mb-6">Send an Inquiry</p>

            {submitted ? (
              <div data-testid="contact-form-success" className="py-16 text-center">
                <p className="font-serif-display italic text-white text-3xl md:text-4xl">Your mail client should now be opening.</p>
                <p className="mt-4 text-white/50">If nothing happens, please email us directly at{" "}
                  <a href={`mailto:${PRIMARY_EMAIL}`} className="text-[color:var(--dk-gold)]">{PRIMARY_EMAIL}</a>
                </p>
                <button type="button" onClick={() => setSubmitted(false)}
                  className="mt-8 text-[11px] uppercase tracking-overline text-white/70 hover:text-white eyebrow-link">
                  Send another
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" noValidate className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Full Name *" error={errors.fullName}>
                    <input type="text" value={form.fullName} onChange={change("fullName")} data-testid="form-name" className={inputCls} />
                  </Field>
                  <Field label="Email *" error={errors.email}>
                    <input type="email" value={form.email} onChange={change("email")} data-testid="form-email" className={inputCls} />
                  </Field>
                  <Field label="Organization">
                    <input type="text" value={form.organization} onChange={change("organization")} data-testid="form-organization" className={inputCls} />
                  </Field>
                  <Field label="Country">
                    <input type="text" value={form.country} onChange={change("country")} data-testid="form-country" className={inputCls} />
                  </Field>
                  <Field label="Inquiry Type">
                    <select value={form.inquiryType} onChange={change("inquiryType")} data-testid="form-inquiry-type" className={inputCls}>
                      {INQUIRY_TYPES.map((t) => <option key={t} value={t} className="bg-black">{t}</option>)}
                    </select>
                  </Field>
                  <Field label="Project of Interest">
                    <select value={form.project} onChange={change("project")} data-testid="form-project" className={inputCls}>
                      <option value="" className="bg-black">— Select —</option>
                      {FILMS.map((f) => <option key={f.slug} value={f.title} className="bg-black">{f.title}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Message *" error={errors.message}>
                  <textarea rows={5} value={form.message} onChange={change("message")} data-testid="form-message" className={`${inputCls} resize-none`} />
                </Field>

                <button type="submit" data-testid="form-submit"
                  className="inline-flex items-center gap-3 bg-[color:var(--dk-red)] hover:bg-[color:var(--dk-red-deep)] text-white px-8 py-4 text-[11px] uppercase tracking-overline transition-colors">
                  Send Inquiry <ArrowUpRight size={14} />
                </button>
              </form>
            )}
          </div>

          {/* Newsletter */}
          <div className="mt-10 border border-white/10 bg-black p-6 md:p-8">
            <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] mb-3">Newsletter</p>
            <h3 className="font-serif-display text-white text-2xl md:text-3xl mb-5">Festival dates, trailer drops, quiet dispatches from set.</h3>
            {newsletterOk ? (
              <p data-testid="newsletter-success" className="text-[color:var(--dk-gold)] text-sm">Thank you — we&apos;ll be in touch.</p>
            ) : (
              <form onSubmit={submitNewsletter} className="flex gap-3" data-testid="newsletter-form">
                <input
                  type="email"
                  required
                  value={newsletter}
                  onChange={(e) => setNewsletter(e.target.value)}
                  placeholder="your@email.com"
                  data-testid="newsletter-input"
                  className="flex-1 bg-transparent border-b border-white/20 focus:border-[color:var(--dk-gold)] text-white text-sm py-2 focus:outline-none transition-colors"
                />
                <button type="submit" data-testid="newsletter-submit"
                  className="inline-flex items-center gap-2 text-[11px] uppercase tracking-overline text-[color:var(--dk-gold)] hover:text-white">
                  Subscribe <Send size={12} />
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </section>
    </main>
  );
}

const inputCls = "w-full bg-transparent border-b border-white/20 focus:border-[color:var(--dk-gold)] text-white text-sm py-3 focus:outline-none transition-colors";

function Field({ label, children, error }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-overline text-white/50 mb-1">{label}</span>
      {children}
      {error && <span className="block text-[10px] text-[color:var(--dk-red)] mt-1">{error}</span>}
    </label>
  );
}

function InfoRow({ icon, label, value, sub }) {
  return (
    <div className="flex items-start gap-4 border-t border-white/10 pt-6">
      <span className="text-[color:var(--dk-gold)] mt-1">{icon}</span>
      <div>
        <p className="text-[10px] uppercase tracking-overline text-white/40 mb-1">{label}</p>
        <p className="font-serif-display text-white text-xl break-all">{value}</p>
        {sub && <p className="text-white/50 text-sm mt-1">{sub}</p>}
      </div>
    </div>
  );
}
